import React from 'react';
import { Callout, Step, DocHeading, DocTable, Shot } from './components';

function PageEsp({ navigate }) {
  return (
    <div>
      <p className="text-sm text-brand-400 font-medium mb-2">Visuals</p>
      <h1 className="font-display text-3xl font-bold text-white tracking-tight mb-3">ESP</h1>
      <p className="text-gray-400 leading-relaxed mb-8">
        ESP draws what is around you straight into the game world: monsters, mercenaries,
        gathering nodes, things you can interact with, loot on the ground, and the spots an enemy
        attack is about to hit. It is also the quickest way to check that the bot sees what you
        see. Everything is on{' '}
        <strong className="text-white">VISUALS &rsaquo; ESP</strong>. While ESP is switched on, a
        green dot appears after <strong className="text-white">VISUALS</strong> in the top bar.
      </p>

      <Callout type="info">
        ESP only draws. It never targets, moves or clicks anything &mdash; turning something on here
        does not make the bot act on it. It also costs nothing while it is off: the bot only
        reads the world for ESP while <strong>Enable</strong> is on.
      </Callout>

      <DocHeading>Getting a Useful Picture</DocHeading>
      <Step n={1} title="Switch on Enable, then set the Range">
        Both are on the <strong>ESP</strong> card. <strong>Range</strong> (10&ndash;150 m, default
        60 m) is how far out it draws. 60 m is a good start; large values on a crowded map turn the
        screen into soup.
      </Step>
      <Step n={2} title="Pick what you care about">
        <strong>CREATURES</strong> and <strong>GATHERING</strong> are simple on/off lists. Turn off
        what you are not farming right now.
      </Step>
      <Step n={3} title="Then trim the details">
        <strong>DETAILS</strong> controls how much hangs off each thing. Boxes, names, level,
        distance and health are separate switches, so you can keep exactly the ones you read.
      </Step>

      <Shot
        name="esp-01-esp-tab.png"
        tab="VISUALS › ESP"
        caption="The ESP tab: what to draw on the left, how to draw it on the right."
        shoot="A VISUALS › ESP fül bekapcsolt ESP-vel (Enable be), játékban. Látszik mind a hat kártya: bal oszlopban ESP, CREATURES, GATHERING, DANGER (a DANGER kártyán a Ground filter sorral), jobb oszlopban DETAILS és TRACERS. A felső sávban a VISUALS felirat után ott a zöld pont."
      />

      <DocHeading>What Can Be Drawn</DocHeading>
      <DocTable
        headers={['Card', 'Switch', 'Default', 'What it shows']}
        rows={[
          [<strong className="text-white">CREATURES</strong>, <strong className="text-white">Monsters</strong>, 'On', 'Enemies around you.'],
          ['', <strong className="text-white">Mercenaries</strong>, 'On', 'Friendly fighters, such as mercenaries and companions.'],
          ['', <strong className="text-white">Dead / depleted</strong>, 'Off', 'Also shows dead monsters (in grey) and nodes that are already harvested or used. Off, those simply disappear.'],
          [<strong className="text-white">GATHERING</strong>, <strong className="text-white">Ores</strong>, 'On', 'Ore nodes.'],
          ['', <strong className="text-white">Plants</strong>, 'On', 'Plant nodes.'],
          ['', <strong className="text-white">Chests</strong>, 'On', 'Chests.'],
          ['', <strong className="text-white">Interactables</strong>, 'On', 'Things you can use: shrines, objects to collect or search, switches, portals, quest objects.'],
          ['', <strong className="text-white">Decoration</strong>, 'Off', 'Torches, gravestones and other scenery, plus objects that could not be sorted into any of the groups above. Mostly noise.'],
          ['', <strong className="text-white">Ground loot</strong>, 'On', 'Items lying on the ground, with their name coloured by rarity.'],
        ]}
      />

      <p className="text-sm text-gray-400 mb-4">
        Every kind has its own colour, so you can tell at a glance what you are looking at.
        Things further away get fainter.
      </p>
      <DocTable
        headers={['Colour', 'Meaning']}
        rows={[
          ['Purple', 'Normal monster'],
          ['Yellow', 'Elite or champion'],
          ['Orange', 'Guardian'],
          ['Red', 'Boss'],
          ['Light blue', 'Mercenary / companion'],
          ['Grey', 'Dead (with Dead / depleted on)'],
          ['Amber · Green · Blue', 'Ore · Plant · Chest'],
          ['Lilac', 'Interactable'],
          ['Dark grey', 'Decoration'],
        ]}
      />

      <p className="text-sm text-gray-400 mb-4">
        <strong className="text-white">What the labels say.</strong> A monster gets two small labels
        stacked above its box: the name (with its level after it) on top, and the distance under
        it. A node shows the game&rsquo;s
        name for it; when the game has no text for that object, you see its internal name or just
        its kind (for example <em>ore</em>, <em>shrine</em> or <em>portal</em>). Ground loot always
        shows the item name &mdash; the <strong>Names</strong> switch does not hide it &mdash; and
        the distance if <strong>Distance</strong> is on.
      </p>
      <p className="text-sm text-gray-400 mb-4">
        <strong className="text-white">Health bars</strong> sit on the left side of the box: green
        above half, yellow above a quarter, red below that. A thin second bar next to it means the
        monster has a shield.
      </p>

      <Shot
        name="22-esp-ingame.png"
        tab="VISUALS › ESP"
        caption="Mercenaries in a crowded area with Boxes switched off: name, distance and health bar, with tracers from the centre of the screen."
        shoot="Régi kép, megtartva — ellenőrizve, hogy a feliratok, az életcsík és a tracerek ma is így néznek ki. Nem kell újra lőni."
      />

      <DocHeading>Danger Zones</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        <strong className="text-white">Attack warnings</strong> (on the{' '}
        <strong className="text-white">DANGER</strong> card) paints the area an incoming enemy
        attack will hit as a red circle on the ground, with a countdown in the middle. This is the
        one ESP option that changes how you play, not just what you see.
      </p>
      <DocTable
        headers={['Setting', 'Default', 'What it does']}
        rows={[
          [<strong className="text-white">Attack warnings</strong>, 'On', 'Shows the incoming areas.'],
          [<strong className="text-white">Ground attacks only</strong>, 'On', 'Only attacks that land on a spot on the ground — the ones you can actually step out of. Leave it on; without it every close-range hit gets a circle too and the screen gets busy.'],
          [<strong className="text-white">Include my own skills</strong>, 'Off', 'Draws your own placed areas as well. Useful when setting up a rotation, noise otherwise.'],
        ]}
      />
      <DocTable
        headers={['What you see', 'What it means']}
        rows={[
          ['A faint red circle', 'An attack is on its way to that spot.'],
          ['The circle turns brighter and its edge thicker', 'Less than two seconds left — move now.'],
          ['The number in the middle', 'Seconds until the attack is over.'],
          [<><strong className="text-white">0.0</strong> and the circle is still there</>, 'The attack is still active. The circle stays full red until the game itself removes it, so a zero never means “safe”.'],
        ]}
      />
      <Callout type="info">
        Warnings are not faded with distance, and they are drawn before everything else: when{' '}
        <strong>Max on screen</strong> runs out, monster boxes, nodes and loot labels are left out
        first. A warning is only cut if the warnings alone fill the whole limit.
      </Callout>

      <p className="text-sm text-gray-400 mb-4">
        <strong className="text-white">The Ground filter line.</strong> Under the switches, the
        DANGER card shows <strong className="text-white">Ground filter</strong> with something like{' '}
        <em>3 of 12 skills</em>: of all the skills active on the map right now (your own
        included), that many were recognised as ground attacks. The numbers change constantly, and
        a low number is normal &mdash; most attacks are not ground attacks. The line only updates
        while both <strong className="text-white">Enable</strong> and{' '}
        <strong className="text-white">Attack warnings</strong> are on.
      </p>
      <DocTable
        headers={['If the card says', 'What to do']}
        rows={[
          [<>Red: <em>&hellip; skill seen, 0 matched - neither name nor address</em></>, 'At that moment none of the active attacks is a ground attack. That is common and harmless during a normal fight. It only matters if it stays red while an enemy is clearly casting ground attacks at you and no circle appears — then ground attacks are not being recognised, usually after a game update. Until the bot is updated, turn off Ground attacks only so you still get warnings (all attacks will show), and let us know.'],
          [<>Grey: <em>name table carries it - the baked address is stale (harmless)</em></>, 'Information only. Warnings work normally; nothing to do.'],
          [<>Orange: <em>address only - the name table did not match any skill</em></>, 'Warnings still work, but through the backup method. Worth reporting.'],
        ]}
      />

      <DocHeading>Appearance</DocHeading>
      <DocTable
        headers={['Setting', 'Card', 'Range', 'What it does']}
        rows={[
          [<strong className="text-white">Range</strong>, 'ESP', '10–150 m', 'How far out it draws. Default 60 m.'],
          [<strong className="text-white">Fade at range</strong>, 'ESP', '0.10–1.00', 'How visible things at the edge of the range stay. Lower = the far ones almost disappear; 1.00 = no fading at all.'],
          [<strong className="text-white">Smoothing</strong>, 'ESP', '0.00–1.00', 'Smooths the movement of monster boxes. Higher = calmer, but slightly behind the monster.'],
          [<strong className="text-white">Boxes</strong>, 'DETAILS', 'On / off', 'Boxes around creatures. Nodes are not affected by this switch.'],
          [<strong className="text-white">Boxes on nodes</strong>, 'DETAILS', 'On / off', 'Boxes around nodes, set separately from Boxes. Off, a node is a small dot with its name only (no distance, no tracer).'],
          [<strong className="text-white">Names · Level · Distance</strong>, 'DETAILS', 'On / off', 'The label lines. Level only shows next to a name, so it needs Names on.'],
          [<strong className="text-white">Health bars · Health percent</strong>, 'DETAILS', 'On / off', 'The bar beside the box, and the percentage next to the distance.'],
          [<strong className="text-white">Line width</strong>, 'DETAILS', '1.0–4.0 px', 'Box outline thickness.'],
          [<strong className="text-white">Box style</strong>, 'DETAILS', 'Corners / Full box', 'Corners is lighter on the eye on a crowded map; Full box outlines the target more clearly.'],
          [<strong className="text-white">Text size</strong>, 'DETAILS', '0.60–2.50', 'Label size.'],
          [<strong className="text-white">Max on screen</strong>, 'DETAILS', '16–400', 'A hard cap on how many things are drawn at once, so a crowded map cannot cost you frames. Default 96.'],
        ]}
      />
      <Callout type="info">
        <strong>Smoothing</strong> and <strong>Text size</strong> are shared with the bot&rsquo;s
        other markers in the world &mdash; for example the pickup circle of{' '}
        <button onClick={() => navigate('auto-loot')} className="text-brand-300 hover:text-brand-200 underline underline-offset-2">Auto Loot</button>{' '}
        &mdash; and they apply even while ESP itself is off.
      </Callout>

      <DocHeading>Tracers</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        On the <strong className="text-white">TRACERS</strong> card.{' '}
        <strong className="text-white">Draw tracers</strong> (off by default) pulls a faint line
        from a point on your screen to every creature, every boxed node and every loot label, which
        helps you find things that are off to the side. Attack warnings, and nodes drawn as a dot
        (with Boxes on nodes off), get no line. <strong className="text-white">Start from</strong> picks that point:{' '}
        <strong>Bottom of screen</strong>, <strong>Centre</strong>, <strong>Top of screen</strong>{' '}
        or <strong>Crosshair</strong>. Crosshair currently starts from the middle of the screen,
        the same as Centre.
      </p>

      <DocHeading>Why Isn&rsquo;t Something Drawn?</DocHeading>
      <DocTable
        headers={['What you notice', 'Why, and what to do']}
        rows={[
          ['Nothing is drawn at all', 'Enable is off, or nothing is within Range. On loading screens nothing is drawn, and right after a map change the first moment is skipped until the bot has looked at the new map.'],
          ['A monster I can see has no box', 'Monsters (or Mercenaries) is off, it is outside Range, or Max on screen has been reached. Objects the game itself will not let you target — thrones, trap shooters, chest blockers — are left out on purpose, and so are invisible helper objects that have no health.'],
          ['A plant I just harvested lost its marker', 'By design: used-up nodes are hidden. Turn on Dead / depleted if you want to see them.'],
          ['Loot labels are missing on a busy map', 'Loot is drawn last, so it is the first thing cut when Max on screen runs out. Raise it, or lower Range. Note that the bot keeps track of at most 96 items on the ground, 128 creatures and 192 nodes at a time, so raising Max on screen past that does not add more.'],
          ['Boxes drift away from what they belong to', 'Usually after changing the field of view on the Camera tab. Try Match ESP projection there; if the boxes sit worse with it on, switch it back off.'],
          ['A node is labelled with a strange internal name', 'The game has no display text for that object, so the bot falls back to its internal name.'],
        ]}
      />
      <Callout type="warning">
        <strong>If your FPS drops</strong>, the settings to touch are <strong>Range</strong> and{' '}
        <strong>Max on screen</strong>, in that order. Ground loot on a busy map is the usual
        culprit.
      </Callout>

      <Callout type="info">
        Loot colours follow the game&rsquo;s own rarity colours &mdash; white, green, blue, violet,
        orange and yellow &mdash; so you can see at a glance whether something worth picking up is
        lying there. Set and mythic items get two extra colours of their own (turquoise and rose),
        which may not match the game exactly. Items without a rarity, like
        gold or materials, use the same light grey as common items. What actually gets picked up is
        decided by{' '}
        <button onClick={() => navigate('auto-loot')} className="text-brand-300 hover:text-brand-200 underline underline-offset-2">Auto Loot</button>;
        for boxes that line up with a changed field of view, see{' '}
        <button onClick={() => navigate('camera')} className="text-brand-300 hover:text-brand-200 underline underline-offset-2">Camera</button>.
      </Callout>
    </div>
  );
}

export default PageEsp;
